import { routes } from "@/config/routes"
import { appendThreadParam } from "@/lib/chat-panel-state"
import { isValidRelativePath } from "@/lib/utils"

/**
 * Whether an authenticated signup should chain into the Slack bot install.
 * Single gate shared by the OAuth callback and the OTP resume path: only
 * "Continue with Slack" signups that created a brand-new tenant qualify, and
 * only from contexts that can do the chain's full-page redirect (not popups).
 */
export function shouldChainSlackInstall(options: {
  onboardingScenario: string | null | undefined
  isNewTenant: boolean | undefined
  canFullPageNavigate: boolean
}): boolean {
  return (
    options.onboardingScenario === "slack_install" &&
    options.isNewTenant === true &&
    options.canFullPageNavigate
  )
}

/**
 * Builds the "Add to Slack" bot-install chain URL. Points at the
 * authenticated Rails connect endpoint (via the /api proxy);
 * `post_connect_path` lands the user on the channel picker, which forwards
 * (`?next=`) to the caller's return path.
 *
 * Used by the Slack signup chain (`entryPoint: "onboarding"`, forwarding to
 * the seeded onboarding chat) and by first-time admin installs from settings
 * (`entryPoint: "admin_connectors_page"`, forwarding back to the settings
 * page).
 */
export function buildSlackInstallChainUrl(options: {
  returnPath?: string | null
  onboardingThreadKey?: string | null
  entryPoint?: "onboarding" | "admin_connectors_page"
}): string {
  const safeReturn =
    options.returnPath && isValidRelativePath(options.returnPath)
      ? options.returnPath
      : routes.okrs.list

  const onboardingReturn = options.onboardingThreadKey
    ? appendThreadParam(safeReturn, options.onboardingThreadKey, {
        chatFullView: true,
      })
    : safeReturn

  // The picker adapts its copy to the entry context (e.g. admin installs get
  // "Go back to Rhythms" instead of signup's "Add more integrations"), so
  // non-signup entries are forwarded on the picker URL.
  const pickerParams = new URLSearchParams({ next: onboardingReturn })
  if (options.entryPoint && options.entryPoint !== "onboarding") {
    pickerParams.set("entry_point", options.entryPoint)
  }
  const postConnectPath = `${routes.onboarding.slackChannels}?${pickerParams.toString()}`

  const chainParams = new URLSearchParams({
    connection_type: "Slack",
    entry_point: options.entryPoint ?? "onboarding",
    post_connect_path: postConnectPath,
  })

  return `/api/users/connect_integration?${chainParams.toString()}`
}
